import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DataTable from '../../components/ui/DataTable';
import AddSupportDocumentModal from '../../components/ui/AddSupportDocumentModal';
import DocumentViewer from '../../components/ui/DocumentViewer';
import DeleteConfirmationModal from '../../components/ui/DeleteConfirmationModal';
import AddTranslationModal from '../../components/ui/AddTranslationModal';
import AddFeeTypeModal from '../../components/ui/AddFeeTypeModal';
import AddFAQModal from '../../components/ui/AddFAQModal';
import ActionMenu from '../../components/ui/ActionMenu';
import RichTextEditor from '../../components/ui/RichTextEditor';
import { createCOA, createDocument, updateDocument, deleteDocument, uploadFileAndGetUrl, getCOA, updateCOA, getCOATerms, putCOATerms, getFees, putFees } from '../../services/coa';
import { toApiTranslationAdminStatus } from '../../utils/translationAdminStatus';

// Legacy organization types (kept for backward compatibility)


// Organization types mapping - all 14 types
const ORGANIZATION_TYPES = [
  { key: 'academiaNonCommercial', label: 'Academia – Non-commercial', shortLabel: 'Academia (Non-commercial)' },
  { key: 'academiaCommerciallyFunded', label: 'Academia – Commercially funded', shortLabel: 'Academia (Commercial)' },
  { key: 'publiclyFundedHealthcareProvider', label: 'Publicly funded healthcare provider', shortLabel: 'Public Healthcare' },
  { key: 'privatelyFundedHealthcareProvider', label: 'Privately funded healthcare provider', shortLabel: 'Private Healthcare' },
  { key: 'publisherEducationalUse', label: 'Publisher – Educational use', shortLabel: 'Publisher (Educational)' },
  { key: 'individualClinicalPracticeNotForProfit', label: 'Individual Clinical Practice – Not for profit', shortLabel: 'Clinical Practice (Non-profit)' },
  { key: 'individualClinicalPracticeForProfit', label: 'Individual Clinical Practice – For profit', shortLabel: 'Clinical Practice (For profit)' },
  { key: 'nutraceuticalCompany', label: 'Nutraceutical Company', shortLabel: 'Nutraceutical' },
  { key: 'contractResearchOrganisation', label: 'Contract Research Organisation (CRO)', shortLabel: 'Contract Research Organisation (CRO)' },
  { key: 'commercialResearchPharma', label: 'Commercial Research – Pharma or medical device company', shortLabel: 'Commercial Research' },
  // { key: 'itCompanyAccessOnly', label: 'IT Company – Access only (No sublicensing)', shortLabel: 'IT Company (Access only)' },
  // { key: 'itCompanyFullRights', label: 'IT Company – Full rights', shortLabel: 'IT Company (Full rights)' },
  { key: 'translatorTranslationCompany', label: 'Translator / Translation Company', shortLabel: 'Translator/Translation' },
];

// Combine all columns for display
const ALL_ORGANIZATION_TYPES = [...ORGANIZATION_TYPES];

const CreateCOA = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('Product Details');
  const [selectedTag, setSelectedTag] = useState('New Translations');
  const [isAddDocumentModalOpen, setIsAddDocumentModalOpen] = useState(false);
  const [isDocumentViewerOpen, setIsDocumentViewerOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isAddTranslationModalOpen, setIsAddTranslationModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [editingDocument, setEditingDocument] = useState(null);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [editingTranslation, setEditingTranslation] = useState(null);
  const [translationToDelete, setTranslationToDelete] = useState(null);
  const [isAddFeeTypeModalOpen, setIsAddFeeTypeModalOpen] = useState(false);
  const [isAddFAQModalOpen, setIsAddFAQModalOpen] = useState(false);
  const [editingFAQ, setEditingFAQ] = useState(null);
  const [faqToDelete, setFaqToDelete] = useState(null);
  const [termsContent, setTermsContent] = useState('');
  const [descriptionContent, setDescriptionContent] = useState('');
  const [organizationType, setOrganizationType] = useState('');
  const [permissionsContent, setPermissionsContent] = useState('');
  const [restrictionsContent, setRestrictionsContent] = useState('');
  const [ecoaNotesContent, setEcoaNotesContent] = useState('');
  const [otherInfoContent, setOtherInfoContent] = useState('');
  const [keyReferencesContent, setKeyReferencesContent] = useState('');
  const [externalLinksContent, setExternalLinksContent] = useState('');
  const [backgroundContent, setBackgroundContent] = useState('');
  const [scoringSystemContent, setScoringSystemContent] = useState('');
  const [developmentMethodologyContent, setDevelopmentMethodologyContent] = useState('');
  const [exampleStudiesContent, setExampleStudiesContent] = useState('');
  const [authorsContent, setAuthorsContent] = useState('');
  const [createdCoaId, setCreatedCoaId] = useState(null);
  const [logoUrl, setLogoUrl] = useState('');
  const [metaTitle, setMetaTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const logoInputRef = useRef(null);
  const { id: routeCoaId } = useParams();
  // Step 1 basic fields
  const [coaName, setCoaName] = useState('');
  const [coaCode, setCoaCode] = useState('');
  const [diseaseOrCondition, setDiseaseOrCondition] = useState('');
  const [details, setDetails] = useState({
    typeOfCOA: '',
    targetAgeGroup: '',
    respondents: '',
    gender: '',
    numItems: '',
    recallPeriod: '',
    responseScales: '',
    domains: '',
    adminTime: '',
    adminModes: '',
    originalDevelopmentLanguage: ''
  });

  // Generic form state for simple scalar inputs (extend as needed)
  const [form, setForm] = useState({
    organizationTypeOptions: [],
    copyright: '',
    tag: '',
    status: 'draft'
  });

  // Generic change handler for flat fields
  const handleChange = (e) => {
    const { name, value, type } = e.target;
    if (!name) return;
    setForm(prev => ({ ...prev, [name]: type === 'number' ? Number(value) : value }));
  };

  // For multi-select checkboxes (organizationTypeOptions example)
  const handleArrayToggle = (name, option) => {
    setForm(prev => {
      const arr = Array.isArray(prev[name]) ? prev[name] : [];
      const exists = arr.includes(option);
      return { ...prev, [name]: exists ? arr.filter(x => x !== option) : [...arr, option] };
    });
  };

  const handleDetailsChange = (name, value) => {
    setDetails(prev => ({ ...prev, [name]: value }));
  };

  // Build payload for create/update from current UI states
  const buildCoaPayload = () => {
    return {
      name: coaName,
      code: coaCode,
      diseaseOrCondition,
      description: descriptionContent,
      organizationType: organizationType || undefined,
      copyright: form.copyright,
      licensePermissions: permissionsContent,
      restrictions: restrictionsContent,
      authors: authorsContent || '',
      details: {
        typeOfCOA: details.typeOfCOA || undefined,
        targetAgeGroup: details.targetAgeGroup || undefined,
        respondents: details.respondents || undefined,
        gender: details.gender || undefined,
        numItems: details.numItems ? Number(details.numItems) : undefined,
        recallPeriod: details.recallPeriod || undefined,
        responseScales: details.responseScales || undefined,
        domains: details.domains || undefined,
        adminTime: details.adminTime || undefined,
        adminModes: details.adminModes || undefined,
        originalDevelopmentLanguage: details.originalDevelopmentLanguage || undefined
      },
      ecoaNotes: ecoaNotesContent || '',
      otherInformation: otherInfoContent || '',
      references: keyReferencesContent || '',
      externalLinks: externalLinksContent || '',
      background: backgroundContent || '',
      scoringSystem: scoringSystemContent || '',
      developmentMethodology: developmentMethodologyContent || '',
      exampleStudies: exampleStudiesContent || '',
      tag: form.tag || selectedTag || undefined,
      logoUrl: logoUrl || undefined,
      metaTitle: metaTitle.trim(),
      metaDescription: metaDescription.trim(),
      status: form.status || 'draft'
    };
  };

  // Load existing COA in edit mode (route id)
  useEffect(() => {
    if (!routeCoaId) return;
    (async () => {
      try {
        const res = await getCOA(routeCoaId);
        const d = res?.data;
        if (!d) return;
        setCreatedCoaId(routeCoaId);
        setCoaName(d.name || '');
        setCoaCode(d.code || '');
        setDiseaseOrCondition(d.diseaseOrCondition || '');
        setSelectedTag(d.tag || '');
        setDescriptionContent(d.description || '');
        setPermissionsContent(d.licensePermissions || '');
        setRestrictionsContent(d.restrictions || '');
        setKeyReferencesContent(d.references || '');
        setExternalLinksContent(d.externalLinks || '');
        setBackgroundContent(d.background || '');
        setScoringSystemContent(d.scoringSystem || '');
        setDevelopmentMethodologyContent(d.developmentMethodology || '');
        setExampleStudiesContent(d.exampleStudies || '');
        setOrganizationType(d.organizationType || '');
        setAuthorsContent(d.authors || '');
        setLogoUrl(d.logoUrl || '');
        setEcoaNotesContent(d.ecoaNotes || '');
        setOtherInfoContent(d.otherInformation || '');
        setMetaTitle(d.metaTitle || '');
        setMetaDescription(d.metaDescription || '');
        // Set form fields for copyright, tag, and status
        setForm(prev => ({
          ...prev,
          copyright: d.copyright || '',
          tag: d.tag || '',
          status: d.status || 'draft'
        }));
        setDetails({
          typeOfCOA: d.details?.typeOfCOA || '',
          targetAgeGroup: d.details?.targetAgeGroup || '',
          respondents: d.details?.respondents || '',
          gender: d.details?.gender || '',
          numItems: d.details?.numItems ?? '',
          recallPeriod: d.details?.recallPeriod || '',
          responseScales: d.details?.responseScales || '',
          domains: d.details?.domains || '',
          adminTime: d.details?.adminTime || '',
          adminModes: d.details?.adminModes || '',
          originalDevelopmentLanguage: d.details?.originalDevelopmentLanguage || ''
        });
      } catch (e) {
        console.error('Failed to load COA', e);
      }
    })();
  }, [routeCoaId]);

  // Load documents when on Support Documents tab and COA id exists
  useEffect(() => {
    if (activeTab !== 'Support Documents') return;
    const id = createdCoaId || routeCoaId;
    if (!id) return;
    (async () => {
      try {
        const res = await (await import('../../services/coa')).listDocuments(id);
        const mapped = (res.data || []).map(d => ({
          id: d._id,
          name: d.name,
          detail: d.detail,
          fee: d.fee ? `£${d.fee}` : '£0.00',
          isLocked: d.isLocked !== undefined ? d.isLocked : false,
          dateCreated: d.createdAt ? new Date(d.createdAt).toLocaleDateString('en-GB') : '',
          lastAction: d.updatedAt ? new Date(d.updatedAt).toLocaleDateString('en-GB') : '',
          user: '',
          avatar: '',
          url: d.fileUrl
        }));
        setDocuments(mapped);
      } catch (e) {
        console.error('Failed to load documents', e);
      }
    })();
  }, [activeTab, createdCoaId, routeCoaId]);

  // Load translations when on Translations tab and COA id exists
  useEffect(() => {
    if (activeTab !== 'Translations') return;
    const id = createdCoaId || routeCoaId;
    if (!id) return;
    (async () => {
      try {
        const res = await (await import('../../services/coa')).listTranslations(id);
        const mapped = (res.data || []).map(t => {
          // Get adminStatus, fallback to status, then to 'pending'
          const adminStatusValue = (t.adminStatus || t.status || 'pending').toLowerCase();
          return {
            id: t._id,
            coaName: t.coaName || coaName || '',
            country: t.country,
            language: t.language,
            status: adminStatusValue,
            adminStatus: adminStatusValue, // Add adminStatus for table display
            tag: t.tag,
            localeTag: t.localeTag || '',
            fee: t.fee ? `£${t.fee}` : '',
            note: t.note || '',
            dateCreated: t.createdAt ? new Date(t.createdAt).toLocaleDateString('en-GB') : '',
            lastAction: t.updatedAt ? new Date(t.updatedAt).toLocaleDateString('en-GB') : '',
            lastActionBy: t.lastActionByUserId?.fullName || '',
            continent: t.continent || '',
            translationDate: t.translationDate || '',
            author: t.author || '',
            fundedBy: t.fundedBy || '',
            method: t.method || '',
            translationFileUrl: t.translationFileUrl || '',
            methodologyFileUrl: t.methodologyFileUrl || '',
            certified: !!t.certified,
            isSourceVersion: !!t.isSourceVersion,
            isLocked: !!t.isLocked
          };
        });
        setTranslations(mapped);
      } catch (e) { console.error('Failed to load translations', e); }
    })();
  }, [activeTab, createdCoaId, routeCoaId, coaName]);

  // Load FAQs when on FAQ's tab and COA id exists
  useEffect(() => {
    if (activeTab !== "FAQ's") return;
    const id = createdCoaId || routeCoaId;
    if (!id) return;
    (async () => {
      try {
        const { listFAQs } = await import('../../services/coa');
        const res = await listFAQs(id);
        const mapped = (res.data || []).map(f => ({
          id: f._id,
          question: f.question,
          answer: f.answer,
          isList: !!f.isList,
          order: f.order !== undefined && f.order !== null ? Number(f.order) : 0
        }));
        setFaqData(mapped);
      } catch (e) { console.error('Failed to load FAQs', e); }
    })();
  }, [activeTab, createdCoaId, routeCoaId]);

  // Load Terms when on Terms tab and COA id exists
  useEffect(() => {
    if (activeTab !== 'Terms & Conditions') return;
    const id = createdCoaId || routeCoaId;
    if (!id) return;
    (async () => {
      try {
        const res = await getCOATerms(id);
        const data = res?.data || {};
        setTermsContent(data.permissionsToUse || '');
        setPermissionsContent(data.licensePermissions || '');
        setRestrictionsContent(data.restrictions || '');
      } catch (e) { console.error('Failed to load terms', e); }
    })();
  }, [activeTab, createdCoaId, routeCoaId]);

  // Load fees when on Fees tab and COA id exists
  useEffect(() => {
    if (activeTab !== 'Fees') return;
    const id = createdCoaId || routeCoaId;
    if (!id) {
      // If no COA ID, reset to empty
      setFeeData({});
      return;
    }
    (async () => {
      try {
        setIsLoadingFees(true);
        const res = await getFees(id);
        console.log('Fees API response:', res);

        // Always update feeData, even if rows is empty
        const transformedFeeData = {};
        if (res && res.data && res.data.rows && Array.isArray(res.data.rows) && res.data.rows.length > 0) {
          // Transform backend structure to frontend structure
          res.data.rows.forEach(row => {
            if (row.name) {
              const feeRow = {};
              // Process both legacy and new columns
              ALL_ORGANIZATION_TYPES.forEach(orgType => {
                // Convert to number, default to 0 if null/undefined
                const value = row[orgType.key];
                feeRow[orgType.key] = value != null ? Number(value) : 0;
              });
              transformedFeeData[row.name] = feeRow;
            }
          });
        }
        console.log('Transformed fee data:', transformedFeeData);
        setFeeData(transformedFeeData);
      } catch (e) {
        console.error('Failed to load fees', e);
        // On error, reset to empty
        setFeeData({});
      } finally {
        setIsLoadingFees(false);
      }
    })();
  }, [activeTab, createdCoaId, routeCoaId]);

  const [faqData, setFaqData] = useState([
    {
      id: 1,
      order: 0,
      question: 'Licence Grant',
      answer: 'You are granted a non-exclusive, non-transferable, limited Licence to use this product for [personal/research/commercial] purposes only, in accordance with these terms.'
    },
    {
      id: 2,
      order: 1,
      question: 'Usage Restrictions',
      answer: 'You may not copy, modify, or distribute the product without prior written consent.\nReverse-engineering, resale, or sublicensing is strictly prohibited.\nThe product must only be used for lawful purposes.',
      isList: true
    },
    {
      id: 3,
      order: 2,
      question: 'Intellectual Property',
      answer: 'All copyrights, trademarks, and proprietary rights remain with the original creators. You may not remove or alter any branding or attributions.'
    },
    {
      id: 4,
      order: 3,
      question: 'Fees & Payments',
      answer: 'If this Licence includes a fee, payment must be made in full before use. Any delays or defaults may result in suspension or termination of the Licence.'
    },
    {
      id: 5,
      order: 4,
      question: 'Liability Disclaimer',
      answer: 'This product is provided \'as-is\' with no warranties. We do not guarantee accuracy, performance, or fitness for a particular purpose.'
    },
    {
      id: 6,
      order: 5,
      question: 'Termination',
      answer: 'We reserve the right to terminate your Licence at any time if these terms are violated. Upon termination, all usage must cease immediately.'
    },
    {
      id: 7,
      order: 6,
      question: 'Governing Law',
      answer: 'This agreement is governed by the laws of [Your Country/Region]. Any disputes will be handled in the appropriate local courts.'
    }
  ]);
  const [feeData, setFeeData] = useState({});
  const [isSavingFees, setIsSavingFees] = useState(false);
  const [isLoadingFees, setIsLoadingFees] = useState(false);
  const [translations, setTranslations] = useState([]);
  const [documents, setDocuments] = useState([]);

  const handleAddDocument = async (newDoc) => {
    if (editingDocument) {
      // Update existing document
      let fileUrl = newDoc.fileUrl || editingDocument.url || editingDocument.fileUrl || '';
      try {
        if (newDoc.file) {
          fileUrl = await uploadFileAndGetUrl(newDoc.file);
        }
        if (editingDocument.id && String(editingDocument.id).length > 10) {
          const feeNumber = Number(String(newDoc.fee || '0').replace(/[^0-9.]/g, '')) || 0;
          await updateDocument(editingDocument.id, {
            name: newDoc.name,
            detail: newDoc.detail,
            fee: feeNumber,
            fileUrl,
            isLocked: newDoc.isLocked !== undefined ? newDoc.isLocked : false
          });
        }
      } catch (e) { console.error('Update document failed', e); }
      setDocuments(documents.map(doc => doc.id === editingDocument.id ? { ...doc, name: newDoc.name, detail: newDoc.detail, fee: newDoc.fee, isLocked: newDoc.isLocked !== undefined ? newDoc.isLocked : false, lastAction: new Date().toLocaleDateString('en-GB'), url: fileUrl || doc.url } : doc));
      setEditingDocument(null);
    } else {
      // Add new document
      const today = new Date().toLocaleDateString('en-GB');
      let fileUrl = '';
      try {
        if (newDoc.file) {
          fileUrl = await uploadFileAndGetUrl(newDoc.file);
        }
        if (createdCoaId) {
          const feeNumber = Number(String(newDoc.fee || '0').replace(/[^0-9.]/g, '')) || 0;
          const res = await createDocument(createdCoaId, {
            name: newDoc.name,
            detail: newDoc.detail,
            fee: feeNumber,
            fileUrl,
            isLocked: newDoc.isLocked || false
          });
          const backend = res?.data;
          if (backend?._id) {
            setDocuments([...documents, {
              id: backend._id,
              name: backend.name,
              detail: backend.detail,
              fee: backend.fee ? `£${backend.fee}` : '£0.00',
              isLocked: backend.isLocked !== undefined ? backend.isLocked : (newDoc.isLocked || false),
              dateCreated: backend.createdAt ? new Date(backend.createdAt).toLocaleDateString('en-GB') : today,
              lastAction: backend.updatedAt ? new Date(backend.updatedAt).toLocaleDateString('en-GB') : today,
              user: 'Current User',
              avatar: 'CU',
              url: backend.fileUrl || fileUrl
            }]);
            return;
          }
        }
      } catch (e) {
        console.error('Create document failed', e);
      }
      const newDocument = {
        id: documents.length + 1,
        name: newDoc.name,
        detail: newDoc.detail,
        fee: newDoc.fee,
        isLocked: newDoc.isLocked || false,
        dateCreated: today,
        lastAction: today,
        user: 'Current User',
        avatar: 'CU',
        url: fileUrl || (newDoc.file ? URL.createObjectURL(newDoc.file) : 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf')
      };
      setDocuments([...documents, newDocument]);
    }
  };

  const handleDocumentAction = (action, row) => {
    const doc = row && row.id ? row : null;

    if (action === 'delete') {
      setDocumentToDelete(doc);
      setIsDeleteModalOpen(true);
    } else if (action === 'edit') {
      setEditingDocument(doc);
      setIsAddDocumentModalOpen(true);
    } else if (action === 'view') {
      setSelectedDocument(doc || undefined);
      setIsDocumentViewerOpen(true);
    }
  };

  const handleDeleteConfirm = () => {
    handleDeleteFAQConfirm();
  };

  const handleCloseModal = () => {
    setIsAddDocumentModalOpen(false);
    setEditingDocument(null);
  };

  const handleAddTranslation = async (newTranslation) => {
    const apiAdminStatus = toApiTranslationAdminStatus(newTranslation.status);
    const status = apiAdminStatus;
    // Upload optional files first
    let translationFileUrl = newTranslation.translationFileUrl || undefined;
    let methodologyFileUrl = newTranslation.methodologyFileUrl || undefined;
    try {
      if (newTranslation.translationFile) {
        translationFileUrl = await uploadFileAndGetUrl(newTranslation.translationFile);
      }
      if (newTranslation.methodologyFile) {
        methodologyFileUrl = await uploadFileAndGetUrl(newTranslation.methodologyFile);
      }
    } catch (e) { console.error('Translation file upload failed', e); }
    if (editingTranslation) {
      try {
        if (editingTranslation && editingTranslation.id) {
          await (await import('../../services/coa')).updateTranslation(editingTranslation.id, {
            coaName: newTranslation.coaName,
            localeTag: newTranslation.localeTag || '',
            country: newTranslation.country,
            language: newTranslation.language,
            adminStatus: apiAdminStatus,
            territory: newTranslation.territory,
            isSourceVersion: newTranslation.isSourceVersion || false,
            continent: newTranslation.continent,
            translationDate: newTranslation.translationDate,
            author: newTranslation.author,
            fundedBy: newTranslation.fundedBy,
            method: newTranslation.method,
            fee: newTranslation.fee || 0,
            isLocked: newTranslation.isLocked !== undefined ? newTranslation.isLocked : false,
            certified: !!newTranslation.certified,
            ...(translationFileUrl && { translationFileUrl }),
            ...(methodologyFileUrl && { methodologyFileUrl })
          });
        }
      } catch (e) { console.error('Update translation failed', e); }
      setTranslations(translations.map(trans => trans.id === editingTranslation.id ? {
        ...trans,
        coaName: newTranslation.coaName || trans.coaName,
        localeTag: newTranslation.localeTag || '',
        country: newTranslation.country,
        language: newTranslation.language,
        status,
        adminStatus: apiAdminStatus,
        territory: newTranslation.territory || trans.territory,
        isSourceVersion: newTranslation.isSourceVersion || false,
        continent: newTranslation.continent || trans.continent,
        translationDate: newTranslation.translationDate || trans.translationDate,
        author: newTranslation.author || trans.author,
        fundedBy: newTranslation.fundedBy || trans.fundedBy,
        method: newTranslation.method || trans.method,
        certified: !!newTranslation.certified,
        isLocked: !!newTranslation.isLocked,
        fee: newTranslation.fee ? `£${newTranslation.fee.toFixed(2)}` : '£0.00',
        ...(translationFileUrl && { translationFileUrl }),
        ...(methodologyFileUrl && { methodologyFileUrl }),
        lastAction: new Date().toLocaleDateString('en-GB')
      } : trans));
      setEditingTranslation(null);
    } else {
      const today = new Date().toLocaleDateString('en-GB');
      let backendId = null;
      const id = createdCoaId || routeCoaId;
      if (id) {
        try {
          const { createTranslation } = await import('../../services/coa');
          const res = await createTranslation(id, {
            coaName: newTranslation.coaName,
            localeTag: newTranslation.localeTag || '',
            country: newTranslation.country,
            language: newTranslation.language,
            adminStatus: apiAdminStatus,
            territory: newTranslation.territory,
            isSourceVersion: newTranslation.isSourceVersion || false,
            continent: newTranslation.continent,
            translationDate: newTranslation.translationDate,
            author: newTranslation.author,
            fundedBy: newTranslation.fundedBy,
            method: newTranslation.method,
            fee: newTranslation.fee || 0,
            isLocked: newTranslation.isLocked || false,
            certified: !!newTranslation.certified,
            ...(translationFileUrl && { translationFileUrl }),
            ...(methodologyFileUrl && { methodologyFileUrl })
          });
          backendId = res?.data?._id || null;
        } catch (e) { console.error('Create translation failed', e); }
      }
      const newTrans = {
        id: backendId || (translations.length + 1),
        coaName: newTranslation.coaName || coaName || '',
        localeTag: newTranslation.localeTag || '',
        country: newTranslation.country,
        language: newTranslation.language,
        status,
        tag: 'IHO Commissioned',
        isLocked: !!newTranslation.isLocked,
        fee: newTranslation.fee ? `£${newTranslation.fee.toFixed(2)}` : '£0.00',
        note: '',
        dateCreated: today,
        lastAction: today,
        lastActionBy: 'Current User',
        avatar: 'CU',
        territory: newTranslation.territory || '',
        isSourceVersion: newTranslation.isSourceVersion || false,
        continent: newTranslation.continent || '',
        translationDate: newTranslation.translationDate || '',
        author: newTranslation.author || '',
        fundedBy: newTranslation.fundedBy || '',
        method: newTranslation.method || '',
        certified: !!newTranslation.certified,
        ...(translationFileUrl && { translationFileUrl }),
        ...(methodologyFileUrl && { methodologyFileUrl })
      };
      setTranslations([...translations, newTrans]);
    }
  };

  const handleTranslationAction = (action, row) => {
    const trans = row && row.id ? row : null;

    if (action === 'delete') {
      setTranslationToDelete(trans);
      setIsDeleteModalOpen(true);
    } else if (action === 'edit') {
      setEditingTranslation(trans);
      setIsAddTranslationModalOpen(true);
    } else if (action === 'view') {
      // Convert translation to document format for viewer
      const translationDoc = {
        name: `${trans.country} - ${trans.language}`,
        detail: `Translation Status: ${trans.status}`,
        fee: trans.fee,
        dateCreated: trans.dateCreated,
        user: trans.lastActionBy,
        url: trans.translationFileUrl || 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf'
      };
      setSelectedDocument(translationDoc);
      setIsDocumentViewerOpen(true);
    }
  };

  const handleDeleteTranslationConfirm = async () => {
    if (translationToDelete) {
      try {
        if (String(translationToDelete.id).length > 10) {
          const { deleteTranslation } = await import('../../services/coa');
          await deleteTranslation(translationToDelete.id);
        }
      } catch (e) { console.error('Delete translation failed', e); }
      setTranslations(translations.filter(trans => trans.id !== translationToDelete.id));
      setTranslationToDelete(null);
    } else if (documentToDelete) {
      try {
        if (documentToDelete.id && String(documentToDelete.id).length > 10) {
          await deleteDocument(documentToDelete.id);
        }
      } catch (e) { console.error('Delete document failed', e); }
      setDocuments(documents.filter(doc => doc.id !== documentToDelete.id));
      setDocumentToDelete(null);
    }
  };

  const handleCloseTranslationModal = () => {
    setIsAddTranslationModalOpen(false);
    setEditingTranslation(null);
  };

  const handleFeeChange = (feeType, category, value) => {
    setFeeData({
      ...feeData,
      [feeType]: {
        ...feeData[feeType],
        [category]: value
      }
    });
  };

  const handleSaveFeeTypes = (newFeeTypes) => {
    // Apply additions AND removals from the modal to the feeData state.
    // Keep existing values for fee types that remain.
    const nextNames = (Array.isArray(newFeeTypes) ? newFeeTypes : [])
      .map((f) => (f?.name || "").trim())
      .filter(Boolean);
    const nextNameSet = new Set(nextNames);

    // Start with existing fee types that are still present
    const updatedFeeData = {};
    Object.keys(feeData).forEach((feeTypeName) => {
      if (nextNameSet.has(feeTypeName)) {
        updatedFeeData[feeTypeName] = feeData[feeTypeName];
      }
    });

    // Add any newly created fee types with default 0 values
    nextNames.forEach((name) => {
      if (!updatedFeeData[name]) {
        const feeRow = {};
        ALL_ORGANIZATION_TYPES.forEach((orgType) => {
          feeRow[orgType.key] = 0;
        });
        updatedFeeData[name] = feeRow;
      }
    });

    setFeeData(updatedFeeData);
  };

  // Save fees to backend
  const handleSaveFees = async () => {
    const id = createdCoaId || routeCoaId;
    if (!id) {
      alert('Please create or select a COA first');
      return;
    }

    try {
      setIsSavingFees(true);

      // Transform frontend structure to backend structure
      const rows = Object.keys(feeData).map(feeTypeName => {
        const row = { name: feeTypeName };
        ALL_ORGANIZATION_TYPES.forEach(orgType => {
          // Convert to number, default to 0
          const value = feeData[feeTypeName][orgType.key];
          row[orgType.key] = value != null ? Number(value) : 0;
        });
        return row;
      });

      await putFees(id, rows);
      alert('Fees saved successfully!');
    } catch (error) {
      console.error('Error saving fees:', error);
      alert('Failed to save fees. Please try again.');
    } finally {
      setIsSavingFees(false);
    }
  };

  const handleAddFAQ = async (newFAQ) => {
    const id = createdCoaId || routeCoaId;
    const orderVal = newFAQ.order !== undefined && newFAQ.order !== null ? Number(newFAQ.order) : 0;
    if (editingFAQ) {
      try {
        if (editingFAQ.id) {
          const { updateFAQ } = await import('../../services/coa');
          await updateFAQ(editingFAQ.id, {
            question: newFAQ.question,
            answer: newFAQ.answer,
            isList: !!newFAQ.isList,
            order: orderVal
          });
        }
      } catch (e) { console.error('Update FAQ failed', e); }
      setFaqData(faqData.map(faq =>
        faq.id === editingFAQ.id
          ? { ...faq, question: newFAQ.question, answer: newFAQ.answer, isList: !!newFAQ.isList, order: orderVal }
          : faq
      ));
      setEditingFAQ(null);
    } else {
      if (!id) { alert('Please create COA first.'); return; }
      try {
        const { createFAQ } = await import('../../services/coa');
        const res = await createFAQ(id, {
          question: newFAQ.question,
          answer: newFAQ.answer,
          isList: !!newFAQ.isList,
          order: orderVal
        });
        const created = res?.data;
        const newFAQItem = {
          id: created?._id || (Math.max(0, ...faqData.map(f => typeof f.id === 'number' ? f.id : 0)) + 1),
          question: created?.question || newFAQ.question,
          answer: created?.answer || newFAQ.answer,
          isList: !!(created?.isList ?? newFAQ.isList),
          order: created?.order !== undefined ? Number(created.order) : orderVal
        };
        setFaqData([...faqData, newFAQItem]);
      } catch (e) { console.error('Create FAQ failed', e); }
    }
  };

  const handleFAQAction = (action, payload) => {
    const faq = (payload && typeof payload === 'object') ? payload : faqData.find(f => f.id === payload);

    if (action === 'edit') {
      if (!faq) return;
      setEditingFAQ(faq);
      setIsAddFAQModalOpen(true);
    } else if (action === 'delete') {
      if (!faq) return;
      setFaqToDelete(faq);
      setIsDeleteModalOpen(true);
    }
  };

  // FAQ Menu Items
  const faqMenuItems = [
    {
      label: 'Edit FAQ',
      action: 'edit',
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M14.1667 2.49998C14.3856 2.28113 14.6454 2.10744 14.9314 1.98899C15.2173 1.87054 15.5238 1.80957 15.8334 1.80957C16.1429 1.80957 16.4494 1.87054 16.7353 1.98899C17.0213 2.10744 17.2811 2.28113 17.5 2.49998C17.7189 2.71884 17.8926 2.97863 18.011 3.2646C18.1295 3.55057 18.1904 3.85706 18.1904 4.16665C18.1904 4.47624 18.1295 4.78273 18.011 5.0687C17.8926 5.35467 17.7189 5.61446 17.5 5.83332L6.25002 17.0833L1.66669 18.3333L2.91669 13.75L14.1667 2.49998Z" stroke="#485B54" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
      danger: false
    },
    {
      label: 'Delete',
      action: 'delete',
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M2.5 5H4.16667H17.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M15.8333 4.99999V16.6667C15.8333 17.1087 15.6577 17.5326 15.3452 17.8452C15.0326 18.1577 14.6087 18.3333 14.1667 18.3333H5.83332C5.39129 18.3333 4.96737 18.1577 4.65481 17.8452C4.34225 17.5326 4.16666 17.1087 4.16666 16.6667V4.99999M6.66666 4.99999V3.33332C6.66666 2.89129 6.84225 2.46737 7.15481 2.15481C7.46737 1.84225 7.89129 1.66666 8.33332 1.66666H11.6667C12.1087 1.66666 12.5326 1.84225 12.8452 2.15481C13.1577 2.46737 13.3333 2.89129 13.3333 3.33332V4.99999" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M8.33334 9.16666V14.1667" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M11.6667 9.16666V14.1667" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
      danger: true
    }
  ];

  const handleDeleteFAQConfirm = () => {
    if (faqToDelete) {
      setFaqData(faqData.filter(faq => faq.id !== faqToDelete.id));
      setFaqToDelete(null);
    } else if (translationToDelete) {
      setTranslations(translations.filter(trans => trans.id !== translationToDelete.id));
      setTranslationToDelete(null);
    } else if (documentToDelete) {
      setDocuments(documents.filter(doc => doc.id !== documentToDelete.id));
      setDocumentToDelete(null);
    }
  };

  const handleCloseFAQModal = () => {
    setIsAddFAQModalOpen(false);
    setEditingFAQ(null);
  };

  const tabs = [
    'Product Details',
    'Support Documents',
    'Translations',
    'Fees',
    "FAQ's",
    'Terms & Conditions'
  ];

  return (
    <>
      <AddSupportDocumentModal
        isOpen={isAddDocumentModalOpen}
        onClose={handleCloseModal}
        onAdd={handleAddDocument}
        editingDocument={editingDocument}
      />

      <DocumentViewer
        isOpen={isDocumentViewerOpen}
        onClose={() => setIsDocumentViewerOpen(false)}
        document={selectedDocument}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setDocumentToDelete(null);
          setTranslationToDelete(null);
          setFaqToDelete(null);
        }}
        onConfirm={async () => {
          // Translation delete
          if (translationToDelete) {
            try {
              if (translationToDelete.id) {
                const { deleteTranslation } = await import('../../services/coa');
                await deleteTranslation(translationToDelete.id);
              }
              setTranslations(prev => prev.filter(t => t.id !== translationToDelete.id));
            } catch (e) {
              console.error('Delete translation failed', e);
            }
            setTranslationToDelete(null);
            setIsDeleteModalOpen(false);
            return;
          }
          // Document delete
          if (documentToDelete) {
            try {
              if (documentToDelete.id && String(documentToDelete.id).length > 10) {
                await deleteDocument(documentToDelete.id);
              }
              setDocuments(prev => prev.filter(d => d.id !== documentToDelete.id));
            } catch (e) {
              console.error('Delete document failed', e);
            }
            setDocumentToDelete(null);
            setIsDeleteModalOpen(false);
            return;
          }
          // FAQ delete
          if (faqToDelete) {
            try {
              if (faqToDelete.id) {
                const { deleteFAQ } = await import('../../services/coa');
                await deleteFAQ(faqToDelete.id);
              }
              setFaqData(prev => prev.filter(f => f.id !== faqToDelete.id));
            } catch (e) { console.error('Delete FAQ failed', e); }
            setFaqToDelete(null);
            setIsDeleteModalOpen(false);
          }
        }}
        title={documentToDelete ? "Delete Document" : translationToDelete ? "Delete Translation" : "Delete FAQ"}
        message={`Are you sure you want to delete this ${documentToDelete ? 'document' : translationToDelete ? 'translation' : 'FAQ'}? This action cannot be undone.`}
        itemName={documentToDelete?.name || (translationToDelete ? `${translationToDelete.country} - ${translationToDelete.language}` : faqToDelete?.question || '')}
        confirmText="Delete"
        cancelText="Cancel"
      />

      <AddTranslationModal
        isOpen={isAddTranslationModalOpen}
        onClose={handleCloseTranslationModal}
        onAdd={handleAddTranslation}
        editingTranslation={editingTranslation}
        currentCoaName={coaName}
      />

      <AddFeeTypeModal
        isOpen={isAddFeeTypeModalOpen}
        onClose={() => setIsAddFeeTypeModalOpen(false)}
        onSave={handleSaveFeeTypes}
        existingFeeTypes={Object.keys(feeData).map((name, index) => ({ id: index + 1, name }))}
      />

      <AddFAQModal
        isOpen={isAddFAQModalOpen}
        onClose={handleCloseFAQModal}
        onAdd={handleAddFAQ}
        editingFAQ={editingFAQ}
        defaultOrder={faqData.reduce((m, f) => Math.max(m, Number(f.order) || 0), -1) + 1}
      />

      <div className="max-w-7xl mx-auto space-y-6 pb-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-900 mb-1">
            {createdCoaId ? 'Edit COA Library entry' : 'Create COA Library entry'}
          </h1>
          <p className="text-sm text-gray-500">
            Information under this section is only available to users once they have signed in.
          </p>
        </div>

        {/* Tabs */}
        <div className="mb-8 bg-[#F2F2F2] rounded-lg p-1">
          <div className="flex gap-1">
            {tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex-1 px-6 py-3 text-sm font-medium transition-all rounded-lg ${activeTab === tab
                  ? 'bg-[#F0FFF2] text-emerald-600'
                  : 'text-gray-600 hover:bg-white/50'
                  }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-lg border border-gray-200 p-10">
          {activeTab === 'Product Details' && (
            <>
              <h2 className="text-lg font-semibold text-gray-900 mb-8">Product Details</h2>

              {/* Licence Logo */}
              <div className="mb-10">
                <label className="block text-sm font-medium text-gray-700 mb-3">Licence Logo</label>
                <div className="flex items-start gap-4">
                  {/* Logo Preview */}
                  <div className="w-20 h-20 rounded-lg bg-gray-100 flex items-center justify-center text-white relative flex-shrink-0 overflow-hidden">
                    {logoUrl ? (
                      <img src={logoUrl} alt="COA Logo" className="w-full h-full object-cover" />
                    ) : (
                      <div className="text-center text-gray-500 text-xs">No Logo</div>
                    )}
                  </div>

                  {/* Upload Buttons */}
                  <div>
                    <div className="flex gap-2 mb-2">
                      <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={async (e) => {
                        const f = e.target.files && e.target.files[0];
                        if (!f) return;
                        try {
                          const url = await uploadFileAndGetUrl(f);
                          setLogoUrl(url);
                          if (createdCoaId) {
                            await updateCOA(createdCoaId, { logoUrl: url });
                          }
                        } catch (err) {
                          console.error('Logo upload failed', err);
                          alert('Logo upload failed');
                        } finally {
                          e.target.value = '';
                        }
                      }} />
                      <button onClick={() => logoInputRef.current && logoInputRef.current.click()} className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-600 rounded-lg text-sm font-medium hover:bg-emerald-100 transition-colors border border-emerald-200">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M8 10.6667V4M8 4L5.33333 6.66667M8 4L10.6667 6.66667" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M2.66667 10.6667V12C2.66667 12.3536 2.80714 12.6928 3.05719 12.9428C3.30724 13.1929 3.64638 13.3333 4 13.3333H12C12.3536 13.3333 12.6928 13.1929 12.9428 12.9428C13.1929 12.6928 13.3333 12.3536 13.3333 12V10.6667" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        Upload new image
                      </button>
                      <button onClick={async () => {
                        setLogoUrl('');
                        if (createdCoaId) {
                          try { await updateCOA(createdCoaId, { logoUrl: '' }); } catch (e) { console.error(e); }
                        }
                      }} className="inline-flex items-center gap-2 px-4 py-2 bg-white text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors border border-gray-300">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M2 4H14M6.66667 7.33333V11.3333M9.33333 7.33333V11.3333M3.33333 4L4 13.3333C4 13.687 4.14048 14.026 4.39052 14.2761C4.64057 14.5262 4.97971 14.6667 5.33333 14.6667H10.6667C11.0203 14.6667 11.3594 14.5262 11.6095 14.2761C11.8595 14.026 12 13.687 12 13.3333L12.6667 4M6 4V2.66667C6 2.48986 6.07024 2.32029 6.19526 2.19526C6.32029 2.07024 6.48986 2 6.66667 2H9.33333C9.51014 2 9.67971 2.07024 9.80474 2.19526C9.92976 2.32029 10 2.48986 10 2.66667V4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        Remove
                      </button>
                    </div>
                    <p className="text-xs text-gray-500">System support PNG and JPG, under 10MB</p>
                  </div>
                </div>
              </div>

              {/* Name, Code, Disease/Condition */}
              <div className="grid grid-cols-3 gap-6 mb-8">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                  <input
                    type="text"
                    value={coaName}
                    onChange={(e) => setCoaName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Acronym / Code</label>
                  <input
                    type="text"
                    value={coaCode}
                    onChange={(e) => setCoaCode(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Disease/Condition</label>
                  <input
                    type="text"
                    value={diseaseOrCondition}
                    onChange={(e) => setDiseaseOrCondition(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Description */}
              <div className="mb-8">
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <RichTextEditor
                  value={descriptionContent}
                  onChange={setDescriptionContent}
                  placeholder="Enter description..."
                />


                {/* Organization Type */}
                {/* <div className="mb-8"> */}
                {/* <label className="block text-sm font-medium text-gray-700 mb-2">Organization Type</label>
          <div className="relative">
            <select
              value={organizationType}
              onChange={(e) => setOrganizationType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent bg-white"
            >
              <option value="">Select organization type</option>
              <option value="Academic research - funded by a pharmaceutical organisation">Academic research - funded by a pharmaceutical organisation</option>
              <option value="Academic research - publicly funded">Academic research - publicly funded</option>
              <option value="Private healthcare provider">Private healthcare provider</option>
              <option value="Public healthcare provider">Public healthcare provider</option>
              <option value="Pharmaceutical company">Pharmaceutical company</option>
              <option value="Biotechnology company">Biotechnology company</option>
              <option value="Contract research organization (CRO)">Contract research organization (CRO)</option>
              <option value="Medical device company">Medical device company</option>
              <option value="Publisher - educational">Publisher - educational</option>
              <option value="Publisher - commercial">Publisher - commercial</option>
              <option value="Non-profit organization">Non-profit organization</option>
              <option value="Government agency">Government agency</option>
              <option value="Other">Other</option>
            </select>
            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M3 4.5L6 7.5L9 4.5" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div> */}
              </div>
              {/* </div> */}

              {/* Copyright & Usage Rights */}
              <div className="mb-6">
                <h3 className="text-base font-semibold text-gray-900 mb-4">Copyright & Usage Rights</h3>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Authors</label>
                    <input
                      value={authorsContent}
                      onChange={(e) => setAuthorsContent(e.target.value)}
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Copyright</label>
                    <input
                      type="text"
                      name="copyright"
                      value={form.copyright}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Permissions to use / Licence */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Permissions to use / Licence</label>
                  <RichTextEditor
                    value={permissionsContent}
                    onChange={setPermissionsContent}
                    placeholder="Enter permissions / licence terms..."
                  />
                </div>

                {/* Titled Restrictions */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Licensing restrictions</label>
                  <RichTextEditor
                    value={restrictionsContent}
                    onChange={setRestrictionsContent}
                    placeholder="Enter licensing restrictions..."
                  />
                </div>
              </div>

              {/* Details */}
              <div className="mb-10">
                <h3 className="text-base font-semibold text-gray-900 mb-6">Details</h3>

                <div className="grid grid-cols-3 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Type of COA</label>
                    <div className="relative">
                      <select value={details.typeOfCOA} onChange={(e) => handleDetailsChange('typeOfCOA', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent bg-white">
                        <option value="">Select</option>
                        <option value="Patient-Reported Outcome Measures (PROMs)">Patient-Reported Outcome Measures (PROMs) </option>
                        <option value="Clinician-Reported Outcome Measures (ClinROs)">Clinician-Reported Outcome Measures (ClinROs) </option>
                        <option value="Observer-Reported Outcome Measures (ObsROs)">Observer-Reported Outcome Measures (ObsROs) </option>
                        <option value="Performance Outcome Measures (PerfOs)">Performance Outcome Measures (PerfOs) </option>
                        <option value="Patient Diary (PD)">Patient Diary (PD) </option>
                      </select>
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                          <path d="M3 4.5L6 7.5L9 4.5" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Tag</label>
                    <div className="relative border border-gray-300 rounded-lg">
                      <select
                        name="tag"
                        value={form.tag || selectedTag}
                        onChange={(e) => {
                          setSelectedTag(e.target.value);
                          handleChange(e);
                        }}
                        className="w-full px-3 py-2 rounded-lg text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-emerald-400 bg-white opacity-0 cursor-pointer relative z-10 border-0"
                      >
                        <option value="">None</option>
                        <option value="new Coa">New COA</option>

                        <option value="New Translations">New Translations Available</option>
                        <option value="updated">Updated</option>
                        <option value="Frequently Requested">Frequently Requested</option>
                        <option value="IHO Managed">IHO Managed</option>
                      </select>
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none z-20">
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                          <path d="M3 4.5L6 7.5L9 4.5" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </div>
                      {/* Dynamic badge overlay */}
                      <div className="absolute inset-0 flex items-center px-3 pointer-events-none">
                        {(form.tag || selectedTag) === 'new Coa' && (
                          <span className="inline-flex items-center px-2.5 py-1 rounded bg-lime-600 text-white text-xs font-medium">
                            New COA
                          </span>
                        )}
                        {(form.tag || selectedTag) === 'New Translations' && (
                          <span className="inline-flex items-center px-2.5 py-1 rounded bg-lime-500 text-white text-xs font-medium">
                            New Translations Available
                          </span>
                        )}
                        {(form.tag || selectedTag) === 'updated' && (
                          <span className="inline-flex items-center px-2.5 py-1 rounded bg-blue-500 text-white text-xs font-medium">
                            Updated
                          </span>
                        )}
                        {(form.tag || selectedTag) === 'Frequently Requested' && (
                          <span className="inline-flex items-center px-2.5 py-1 rounded bg-orange-500 text-white text-xs font-medium">
                            Frequently Requested
                          </span>
                        )}
                        {(form.tag || selectedTag) === 'IHO Managed' && (
                          <span className="inline-flex items-center px-2.5 py-1 rounded bg-indigo-500 text-white text-xs font-medium">
                            IHO Managed
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                    <div className="relative">
                      <select
                        name="status"
                        value={form.status}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent bg-white"
                      >
                        <option value="draft">Draft</option>
                        <option value="published">Published</option>
                        <option value="archived">Archived</option>
                      </select>
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                          <path d="M3 4.5L6 7.5L9 4.5" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Second row */}
                <div className="grid grid-cols-3 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Respondent age group</label>
                    <div className="relative">
                      <select value={details.targetAgeGroup} onChange={(e) => handleDetailsChange('targetAgeGroup', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent bg-white">
                        <option value="">Select</option>
                        <option>Children (0-12)</option>
                        <option>Teens (13-17)</option>
                        <option>Adults (18+)</option>
                        <option>Seniors (65+)</option>
                      </select>
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                          <path d="M3 4.5L6 7.5L9 4.5" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Respondents</label>
                    <input
                      type="text"
                      value={details.respondents}
                      onChange={(e) => handleDetailsChange('respondents', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
                    <div className="relative">
                      <select value={details.gender} onChange={(e) => handleDetailsChange('gender', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent bg-white">
                        <option value="">Select</option>
                        <option>Male</option>
                        <option>Female</option>
                        <option>Other</option>
                        <option>All</option>
                      </select>
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                          <path d="M3 4.5L6 7.5L9 4.5" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Number of Items</label>
                    <input
                      type="number"
                      value={details.numItems}
                      onChange={(e) => handleDetailsChange('numItems', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Third row */}
                <div className="grid grid-cols-3 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Recall period</label>
                    <input
                      type="text"
                      value={details.recallPeriod}
                      onChange={(e) => handleDetailsChange('recallPeriod', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text sm font-medium text-gray-700 mb-2">Response scales</label>
                    <input
                      type="text"
                      value={details.responseScales}
                      onChange={(e) => handleDetailsChange('responseScales', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Domains / concepts measured</label>
                    <input
                      type="text"
                      value={details.domains}
                      onChange={(e) => handleDetailsChange('domains', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Fourth row */}
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Administration time</label>
                    <input
                      type="text"
                      value={details.adminTime}
                      onChange={(e) => handleDetailsChange('adminTime', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Administration Modes</label>
                    <input
                      type="text"
                      value={details.adminModes}
                      onChange={(e) => handleDetailsChange('adminModes', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Notes for eCOA reproductions and Other Information */}
              <div className="grid grid-cols-2 gap-6 mb-10">
                <div className="mb-8">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Notes for eCOA reproductions</label>
                  <RichTextEditor
                    value={ecoaNotesContent}
                    onChange={setEcoaNotesContent}
                    placeholder="Enter Notes..."

                  />
                </div>
                <div className="mb-8">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Additional details</label>
                  <RichTextEditor
                    value={otherInfoContent}
                    onChange={setOtherInfoContent}
                    placeholder="Enter Notes..."

                  />
                </div>


              </div>

              {/* References & External Resources */}
              <div className="mb-10">
                <h3 className="text-base font-semibold text-gray-900 mb-6">References & External Resources</h3>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Original Development Language</label>
                  <input
                    type="text"
                    name="originalDevelopmentLanguage"
                    value={details.originalDevelopmentLanguage}
                    onChange={(e) => handleDetailsChange('originalDevelopmentLanguage', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
                  />
                </div>

                {/* Key references and Useful external links */}
                <div className="mb-8">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Key references</label>
                  <RichTextEditor
                    value={keyReferencesContent}
                    onChange={setKeyReferencesContent}
                    placeholder="Enter Notes..."

                  />
                </div>


              </div>

              {/* Background */}


              <div className="mb-8">
                <label className="block text-sm font-medium text-gray-700 mb-2">Background</label>
                <RichTextEditor
                  value={backgroundContent}
                  onChange={setBackgroundContent}
                  placeholder="Enter Notes..."

                />
              </div>
              <div className="mb-8">
                <label className="block text-sm font-medium text-gray-700 mb-2">Scoring System</label>
                <RichTextEditor
                  value={scoringSystemContent}
                  onChange={setScoringSystemContent}
                  placeholder="Enter Notes..."

                />
              </div>

              {/* Scoring System */}


              {/* Development Methodology */}
              <div className="mb-8">
                <label className="block text-sm font-medium text-gray-700 mb-2">Development Methodology</label>
                <RichTextEditor
                  value={developmentMethodologyContent}
                  onChange={setDevelopmentMethodologyContent}
                  placeholder="Enter Notes..."

                />
              </div>


              {/* Example Studies */}

              <div className="mb-8">
                <label className="block text-sm font-medium text-gray-700 mb-2">Example Studies</label>
                <RichTextEditor
                  value={exampleStudiesContent}
                  onChange={setExampleStudiesContent}
                  placeholder="Enter Notes..."

                />
              </div>
            </>
          )}

          {/* Documents Tab */}
          {activeTab === 'Support Documents' && (
            <>
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-lg font-semibold text-gray-900">Support Documents List</h2>
                <button
                  onClick={() => setIsAddDocumentModalOpen(true)}
                  className="px-6 py-2.5 bg-emerald-400 text-white rounded-lg text-sm font-medium hover:bg-emerald-500 transition-colors shadow-sm"
                >
                  Add Document
                </button>
              </div>

              {/* Documents Table using DataTable component */}
              <DataTable
                columns={[
                  { header: 'Document Name', accessor: 'name' },
                  // { header: 'Document Detail', accessor: 'detail', className: 'max-w-xs truncate' },
                  { header: 'Fee', accessor: 'fee', className: 'font-semibold' },
                  { header: 'Date Created', accessor: 'dateCreated' },
                  { header: 'Last Action', accessor: 'lastAction' },
                  { header: 'Last Action by', accessor: 'user' }
                ]}
                data={documents}
                rowsPerPage={documents.length || 1000}

                renderCell={(accessor, row, column) => {
                  if (accessor === 'user') {
                    return (
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-emerald-400 text-white text-xs flex items-center justify-center font-medium">
                          {row.avatar}
                        </div>
                        <span className="text-sm text-gray-900">{row.user}</span>
                      </div>
                    );
                  }
                  return (
                    <span className={`text-sm ${column.className || 'text-gray-900'}`}>
                      {row[accessor]}
                    </span>
                  );
                }}
                actionMenuItems={[
                  {
                    label: 'Edit Document',
                    icon: (
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M9.16667 1.66667H7.5C3.33333 1.66667 1.66667 3.33334 1.66667 7.5V12.5C1.66667 16.6667 3.33333 18.3333 7.5 18.3333H12.5C16.6667 18.3333 18.3333 16.6667 18.3333 12.5V10.8333" stroke="#73827c" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M13.3667 2.51667L6.80001 9.08334C6.55001 9.33334 6.30001 9.825 6.25001 10.1833L5.89168 12.6917C5.75834 13.6 6.40001 14.2333 7.30834 14.1083L9.81668 13.75C10.1667 13.7 10.6583 13.45 10.9167 13.2L17.4833 6.63334C18.6167 5.5 19.15 4.18334 17.4833 2.51667C15.8167 0.850002 14.5 1.38334 13.3667 2.51667Z" stroke="#73827c" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    ),
                    action: 'edit',
                    danger: false
                  },
                  {
                    label: 'View Document',
                    icon: (
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M12.9833 10C12.9833 11.65 11.65 12.9833 10 12.9833C8.35 12.9833 7.01666 11.65 7.01666 10C7.01666 8.35 8.35 7.01666 10 7.01666C11.65 7.01666 12.9833 8.35 12.9833 10Z" stroke="#73827c" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M9.99999 16.8917C12.9417 16.8917 15.6833 15.1583 17.5917 12.1583C18.3417 10.9833 18.3417 9.00833 17.5917 7.83333C15.6833 4.83333 12.9417 3.1 9.99999 3.1C7.05832 3.1 4.31666 4.83333 2.40832 7.83333C1.65832 9.00833 1.65832 10.9833 2.40832 12.1583C4.31666 15.1583 7.05832 16.8917 9.99999 16.8917Z" stroke="#73827c" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    ),
                    action: 'view',
                    danger: false
                  },
                  {
                    label: 'Delete',
                    icon: (
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M17.5 4.98333C14.725 4.70833 11.9333 4.56667 9.15 4.56667C7.5 4.56667 5.85 4.65 4.2 4.81667L2.5 4.98333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M7.08334 4.14167L7.26668 3.05C7.40001 2.25833 7.50001 1.66667 8.90834 1.66667H11.0917C12.5 1.66667 12.6083 2.29167 12.7333 3.05833L12.9167 4.14167" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M15.7083 7.61667L15.1667 16.0083C15.075 17.3167 15 18.3333 12.675 18.3333H7.32499C4.99999 18.3333 4.92499 17.3167 4.83332 16.0083L4.29166 7.61667" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M8.60834 13.75H11.3833" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M7.91666 10.4167H12.0833" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    ),
                    action: 'delete',
                    danger: true
                  }
                ]}
                onRowAction={handleDocumentAction}
                showPagination={false}
              />
            </>
          )}

          {/* Translations Tab */}
          {activeTab === 'Translations' && (
            <>
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Translations</h2>
                <button
                  onClick={() => setIsAddTranslationModalOpen(true)}
                  className="px-5 py-2.5 bg-emerald-400 text-white rounded-lg text-sm font-medium hover:bg-emerald-500 transition-colors shadow-sm flex items-center gap-2"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M8 3.33334V12.6667" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M3.33334 8H12.6667" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Add Translation
                </button>
              </div>

              {/* Translations Table */}
              <DataTable
                columns={[
                  { header: 'Country', accessor: 'country' },
                  { header: 'Language', accessor: 'language' },
                  { header: 'Admin Status', accessor: 'adminStatus' },
                  { header: 'Source', accessor: 'country' },
                  { header: 'Fees', accessor: 'fee' },
                  { header: 'Note', accessor: 'note' },
                  { header: 'Date Created', accessor: 'dateCreated' },
                  { header: 'Last Action', accessor: 'lastAction' },
                  { header: 'Last Action by', accessor: 'lastActionBy' }
                ]}
                data={translations}
                rowsPerPage={translations.length || 1000}
                renderCell={(accessor, row, column) => {
                  if (accessor === 'adminStatus') {
                    const statusConfig = {
                      approved: {
                        bg: 'bg-[#E8F5E9]',
                        text: 'text-[#2E7D32]',
                        border: 'border-[#81C784]',
                        dot: 'bg-[#4CAF50]',
                        label: 'Approved'
                      },
                      pending: {
                        bg: 'bg-[#E3F2FD]',
                        text: 'text-[#1565C0]',
                        border: 'border-[#64B5F6]',
                        dot: 'bg-[#2196F3]',
                        label: 'In Development'
                      },
                      rejected: {
                        bg: 'bg-[#FFEBEE]',
                        text: 'text-[#C62828]',
                        border: 'border-[#E57373]',
                        dot: 'bg-[#F44336]',
                        label: 'Rejected'
                      },
                      in_development: {
                        bg: 'bg-[#E3F2FD]',
                        text: 'text-[#1565C0]',
                        border: 'border-[#64B5F6]',
                        dot: 'bg-[#2196F3]',
                        label: 'In Development'
                      }
                    };
                    // Use adminStatus from row, fallback to status, then to 'pending'
                    const actualStatus = (row.adminStatus || row.status || 'pending').toLowerCase();
                    const config = statusConfig[actualStatus] || statusConfig.pending;
                    return (
                      <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border ${config.bg} ${config.text} ${config.border}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`}></span>
                        {config.label}
                      </span>
                    );
                  }
                  if (accessor === 'tag') {
                    const tagConfig = {
                      'IHO Commissioned': { color: 'text-[#7CB342]' },
                      'Translate Themselves': { color: 'text-[#FF9800]' },
                      'Form Library': { color: 'text-[#E53935]' }
                    };
                    const tagLabel = { 'new Coa': 'New COA' };
                    const config = tagConfig[row.tag] || { color: 'text-gray-600' };
                    return (
                      <span className={`text-xs font-normal ${config.color}`}>
                        {tagLabel[row.tag] ?? row.tag}
                      </span>
                    );
                  }
                  if (accessor === 'lastActionBy') {
                    return (
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-emerald-400 text-white text-xs flex items-center justify-center font-medium">
                          {row.avatar}
                        </div>
                        <span className="text-sm text-gray-900">{row.lastActionBy}</span>
                      </div>
                    );
                  }
                  if (accessor === 'fee') {
                    return (
                      <span className="text-sm text-gray-600">
                        {row[accessor]}
                      </span>
                    );
                  }
                  if (accessor === 'note') {
                    return (
                      <span className="text-sm text-gray-500 max-w-xs truncate block">
                        {row[accessor]}
                      </span>
                    );
                  }
                  return (
                    <span className={`text-sm ${column.className || 'text-gray-900'}`}>
                      {row[accessor]}
                    </span>
                  );
                }}
                actionMenuItems={[
                  {
                    label: 'Edit Translation',
                    icon: (
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M14.1667 2.49998C14.3856 2.28113 14.6454 2.10744 14.9314 1.98899C15.2173 1.87054 15.5238 1.80957 15.8334 1.80957C16.1429 1.80957 16.4494 1.87054 16.7353 1.98899C17.0213 2.10744 17.2811 2.28113 17.5 2.49998C17.7189 2.71884 17.8926 2.97863 18.011 3.2646C18.1295 3.55057 18.1904 3.85706 18.1904 4.16665C18.1904 4.47624 18.1295 4.78273 18.011 5.0687C17.8926 5.35467 17.7189 5.61446 17.5 5.83332L6.25002 17.0833L1.66669 18.3333L2.91669 13.75L14.1667 2.49998Z" stroke="#485B54" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    ),
                    action: 'edit'
                  },
                  {
                    label: 'View Translation',
                    icon: (
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M0.833344 9.99998C0.833344 9.99998 4.16668 3.33331 10 3.33331C15.8333 3.33331 19.1667 9.99998 19.1667 9.99998C19.1667 9.99998 15.8333 16.6666 10 16.6666C4.16668 16.6666 0.833344 9.99998 0.833344 9.99998Z" stroke="#485B54" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M10 12.5C11.3807 12.5 12.5 11.3807 12.5 10C12.5 8.61929 11.3807 7.5 10 7.5C8.61929 7.5 7.5 8.61929 7.5 10C7.5 11.3807 8.61929 12.5 10 12.5Z" stroke="#485B54" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    ),
                    action: 'view'
                  },
                  {
                    label: 'Delete',
                    icon: (
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M2.5 5H4.16667H17.5" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M15.8333 4.99999V16.6667C15.8333 17.1087 15.6577 17.5326 15.3452 17.8452C15.0326 18.1577 14.6087 18.3333 14.1667 18.3333H5.83332C5.39129 18.3333 4.96737 18.1577 4.65481 17.8452C4.34225 17.5326 4.16666 17.1087 4.16666 16.6667V4.99999M6.66666 4.99999V3.33332C6.66666 2.89129 6.84225 2.46737 7.15481 2.15481C7.46737 1.84225 7.89129 1.66666 8.33332 1.66666H11.6667C12.1087 1.66666 12.5326 1.84225 12.8452 2.15481C13.1577 2.46737 13.3333 2.89129 13.3333 3.33332V4.99999" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M8.33334 9.16666V14.1667" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M11.6667 9.16666V14.1667" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    ),
                    action: 'delete',
                    className: 'text-red-600 hover:bg-red-50'
                  }
                ]}
                onRowAction={handleTranslationAction}
                showPagination={false}
              />
            </>
          )}

          {/* Fee Tab */}
          {activeTab === 'Fees' && (
            <>
              {/* Header with Edit Button */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2.5">
                  <h2 className="text-xl font-semibold text-gray-900">Fees</h2>
                  <button
                    onClick={() => setIsAddFeeTypeModalOpen(true)}
                    className="flex items-center justify-center w-9 h-9 rounded-full border-2 border-emerald-400 text-emerald-400 hover:bg-emerald-50 transition-colors"
                  >
                    <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
                      <path d="M8 3.33334V12.6667" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M3.33334 8H12.6667" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </div>
                <button
                  onClick={handleSaveFees}
                  disabled={isSavingFees}
                  className="px-5 py-2.5 bg-emerald-400 text-white rounded-lg text-sm font-medium hover:bg-emerald-500 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSavingFees ? 'Saving...' : 'Save Table'}
                </button>
              </div>

              {/* Fee Table */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  {isLoadingFees ? (
                    <div className="p-8 text-center text-gray-500">Loading fees...</div>
                  ) : Object.keys(feeData).length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                      No fees configured. Click the + button to add fee types.
                    </div>
                  ) : (
                    <table className="w-full min-w-[1400px]">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-200">
                          <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 sticky left-0 bg-gray-50 z-10">Fee Type</th>
                          {ALL_ORGANIZATION_TYPES.map((orgType) => (
                            <th key={orgType.key} className="px-4 py-4 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">
                              <div>{orgType.shortLabel}</div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {Object.keys(feeData).map((feeType, index) => (
                          <tr key={feeType} className={`${index !== Object.keys(feeData).length - 1 ? 'border-b border-gray-200' : ''} hover:bg-gray-50`}>
                            <td className="px-6 py-4 text-sm text-gray-700 font-medium sticky left-0 bg-white z-10">{feeType}</td>
                            {ALL_ORGANIZATION_TYPES.map((orgType) => (
                              <td key={orgType.key} className="px-4 py-4 text-center">
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={feeData[feeType][orgType.key] || ''}
                                  onChange={(e) => {
                                    const value = e.target.value === '' ? 0 : parseFloat(e.target.value) || 0;
                                    handleFeeChange(feeType, orgType.key, value);
                                  }}
                                  className="w-full max-w-[120px] px-3 py-2 text-sm text-center text-gray-900 bg-transparent border border-gray-300 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-400 rounded transition-all mx-auto"
                                />
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </>
          )}

          {/* FAQ's Tab */}
          {activeTab === "FAQ's" && (
            <>
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">FAQ's</h2>
                <button
                  onClick={() => setIsAddFAQModalOpen(true)}
                  className="px-5 py-2.5 bg-emerald-400 text-white rounded-lg text-sm font-medium hover:bg-emerald-500 transition-colors shadow-sm"
                >
                  Add FAQ
                </button>
              </div>

              {/* FAQ Items (sorted by display order) */}
              <div className="space-y-6">
                {[...faqData].sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0)).map((faq) => (
                  <div key={faq.id} className="bg-white rounded-xl border border-gray-200 overflow-visible">
                    <div className="flex items-start justify-between px-6 py-5 gap-4">
                      <div className="flex-1">
                        <p className="text-xs text-gray-500 mb-1">Order {faq.order ?? 0}</p>
                        <h3 className="text-base font-semibold text-gray-900 mb-3">{faq.question}</h3>
                        {faq.isList ? (
                          <ul className="list-disc list-inside text-sm text-gray-600 space-y-2">
                            {faq.answer.split('\n').map((item, idx) => (
                              <li key={idx}>{item}</li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-sm text-gray-600 leading-relaxed">{faq.answer}</p>
                        )}
                      </div>
                      <ActionMenu
                        items={faqMenuItems}
                        onAction={handleFAQAction}
                        itemId={faq.id}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Terms & Conditions Tab */}
          {activeTab === 'Terms & Conditions' && (
            <>
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-2">Licence (Permissions)  Terms and Conditions</h2>
              </div>

              {/* Rich Text Editor */}
              <RichTextEditor
                value={termsContent}
                onChange={setTermsContent}
                placeholder="Start typing your terms and conditions..."
              />
            </>
          )}
        </div>

        {/* SEO Settings (public website): own card, saved with Product Details */}
        {activeTab === 'Product Details' && (
          <div className="mt-6 bg-white rounded-lg border border-gray-200 p-10">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">SEO Settings</h2>
            <p className="text-sm text-gray-500 mb-8">
              Shown in search results for this COA's page on the InSpired website. Leave blank to use the COA name and description.
            </p>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Meta Title</label>
              <input
                type="text"
                name="metaTitle"
                value={metaTitle}
                onChange={(e) => setMetaTitle(e.target.value)}
                placeholder={coaName ? `${coaName} | InSpired Health Outcomes` : 'e.g. Back Beliefs Questionnaire (BBQ) | InSpired Health Outcomes'}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
              />
              <p className={`text-xs mt-1 ${metaTitle.length > 60 ? 'text-amber-600' : 'text-gray-500'}`}>
                {metaTitle.length}/60 characters recommended{metaTitle.length > 60 ? ' — Google may truncate longer titles' : ''}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Meta Description</label>
              <textarea
                name="metaDescription"
                value={metaDescription}
                onChange={(e) => setMetaDescription(e.target.value)}
                rows={3}
                placeholder="One or two sentences summarising this COA for search results."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
              />
              <p className={`text-xs mt-1 ${metaDescription.length > 160 ? 'text-amber-600' : 'text-gray-500'}`}>
                {metaDescription.length}/160 characters recommended{metaDescription.length > 160 ? ' — Google may truncate longer descriptions' : ''}
              </p>
            </div>
          </div>
        )}

        {/* Footer Actions */}
        <div className="flex items-center justify-between pt-6 mt-6 border-t border-gray-200">
          <button
            onClick={() => {
              const currentIndex = tabs.indexOf(activeTab);
              if (currentIndex > 0) {
                setActiveTab(tabs[currentIndex - 1]);
              } else {
                navigate(-1);
              }
            }}
            className="px-6 py-2.5 text-gray-600 text-sm font-medium hover:text-gray-900 transition-colors"
          >
            Back
          </button>
          <button
            onClick={async () => {
              const currentIndex = tabs.indexOf(activeTab);
              if (activeTab === 'Product Details') {
                const name = coaName.trim();
                const code = coaCode.trim();
                if (!name || !code) { alert('Please enter Name and Code'); return; }
                const payload = buildCoaPayload();
                try {
                  if (!createdCoaId) {
                    const res = await createCOA(payload);
                    const id = res?.data?._id;
                    if (id) setCreatedCoaId(id);
                  } else {
                    await updateCOA(createdCoaId, payload);
                  }
                } catch (e) { console.error('Save COA failed', e); alert('Failed to save COA'); return; }
              }

              if (currentIndex < tabs.length - 1) {
                setActiveTab(tabs[currentIndex + 1]);
              } else {
                if (!createdCoaId) { alert('Please create COA first'); return; }
                // Save terms before final submit
                try {
                  await putCOATerms(createdCoaId, { permissionsToUse: termsContent, restrictions: restrictionsContent });
                } catch (e) { console.error('Failed to save terms', e); }
                navigate(`/coa-preview/${createdCoaId}`);
              }
            }}
            className="px-6 py-2.5 bg-emerald-400 text-white rounded-lg text-sm font-medium hover:bg-emerald-500 transition-colors shadow-sm"
          >
            {activeTab === 'Terms & Conditions' ? 'Submit' : 'Continue'}
          </button>
        </div>
      </div>
    </>
  );
};

export default CreateCOA;


